import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Loader2,
  MessageCircle,
  Linkedin,
  Instagram,
  Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHITEPAPER_URL =
  "https://customer-assets.emergentagent.com/job_tour-innovation-hub/artifacts/394o6yxc_El%20futuro%20no%20esta%CC%81%20en%20dejar%20de%20viajar%2C%20sino%20en%20la%20reingenieri%CC%81a%20total%20de%20la%20experiencia.%20Debemos%20transitar%20de%20un%20turismo%20extractivo%20a%20uno%20regenerativo%2C%20para%20disen%CC%83ar%20destinos%20inteligentes%20que%20operen%20.pdf";

/* TODO: Replace these with Tony's real handles */
const SOCIALS = [
  {
    id: "whatsapp",
    label: "Escríbele por WhatsApp",
    sub: "+52 55 4360 7330",
    icon: MessageCircle,
    href: "https://wa.me/525543607330",
    primary: true,
  },
  {
    id: "linkedin",
    label: "Sígue en LinkedIn",
    sub: "@tony-yarto",
    icon: Linkedin,
    href: "https://www.linkedin.com/in/tonyyarto/",
  },
  {
    id: "instagram",
    label: "Sígue en Instagram",
    sub: "@tonyyarto",
    icon: Instagram,
    href: "https://instagram.com/tonyyarto",
  },
  {
    id: "email",
    label: "Escríbele al correo",
    sub: "tony@incubatour.com",
    icon: Mail,
    href: "mailto:tony@incubatour.com",
  },
];

function TurtopiaHeader() {
  return (
    <header
      data-testid="turtopia-header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="turtopia-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white"
        >
          <ArrowLeft size={14} /> Tony Yarto
        </Link>
        <div className="font-display font-black tracking-tighter text-sm md:text-base uppercase">
          Turtopía
        </div>
        <a
          href="#descargar"
          data-testid="turtopia-header-cta"
          className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/70 hover:text-accent transition-colors"
        >
          Descargar →
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section
      data-testid="turtopia-hero"
      className="relative min-h-[80svh] flex items-center justify-center bg-black noise overflow-hidden"
    >
      {/* Subtle accent glow */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(228,0,124,0.08), transparent 55%)",
        }}
      />
      <div className="absolute inset-0 bg-grid opacity-20 -z-10" />

      <div className="relative max-w-3xl mx-auto px-6 md:px-8 py-24 md:py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-white/5 text-[10px] uppercase tracking-[0.3em] text-white/70 mb-10"
        >
          <span className="h-1.5 w-1.5 bg-accent rounded-full animate-pulse" />
          Colón · Panamá · 2026
        </motion.div>

        <motion.h1
          data-testid="turtopia-headline"
          className="font-display uppercase font-black tracking-tighter leading-[0.9]"
        >
          <motion.span
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="block text-[clamp(3rem,9vw,6rem)]"
          >
            Turtopía.
          </motion.span>
          <motion.span
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="block text-[clamp(1.8rem,4vw,3rem)] font-light italic text-white/55 mt-2"
          >
            La anatomía del impacto.
          </motion.span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-10 text-base md:text-lg text-white/65 leading-relaxed max-w-xl mx-auto"
        >
          La charla de Tony Yarto sobre los factores ocultos de la huella de
          carbono del turismo —{" "}
          <span className="text-white">y cómo transitar del modelo
          extractivo al regenerativo</span>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-10"
        >
          <a
            href="#descargar"
            data-testid="hero-download-cta"
            className="btn-block"
          >
            <Download size={14} /> Descargar el white paper
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function Socials() {
  return (
    <section
      id="conecta"
      data-testid="socials-section"
      className="relative bg-[#070707] border-t border-white/10 py-20 md:py-24"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/55">
            <span className="h-px w-8 bg-accent" />
            <span className="text-accent">Antes de descargar</span>
            <span className="h-px w-8 bg-accent" />
          </div>
          <h2 className="font-display mt-5 text-3xl md:text-4xl font-black tracking-tighter uppercase leading-tight">
            Conecta con Tony.
          </h2>
          <p className="mt-3 text-white/60 max-w-md mx-auto text-sm md:text-base">
            Sígue su trabajo o escríbele directo. No hay nada como una
            conversación real.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
          {SOCIALS.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.id}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-${s.id}`}
                className={`group flex items-center gap-4 p-4 md:p-5 border transition-all ${
                  s.primary
                    ? "border-accent/40 bg-accent-soft hover:bg-accent hover:border-accent"
                    : "border-white/10 bg-[#0a0a0a] hover:border-white/30 hover:bg-[#111]"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-10 h-10 shrink-0 border ${
                    s.primary
                      ? "border-accent bg-black/30 text-accent group-hover:bg-white group-hover:text-accent"
                      : "border-white/15 text-white"
                  }`}
                >
                  <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm md:text-base font-bold uppercase tracking-tight">
                    {s.label}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mt-0.5">
                    {s.sub}
                  </div>
                </div>
                <span className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all">
                  →
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DescargaForm() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y correo son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        interest: "Turtopía · White paper",
        source: "turtopia",
      });
      setSubmitted(true);
      toast.success("¡Listo! Tu white paper está disponible para descargar.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Algo salió mal. Inténtalo de nuevo en unos minutos."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="descargar"
      data-testid="descarga-section"
      className="relative bg-black border-t border-white/10 py-20 md:py-28"
    >
      <div className="max-w-xl mx-auto px-6 md:px-8">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-accent" />
            <span className="text-accent">Descarga gratuita</span>
            <span className="h-px w-8 bg-accent" />
          </div>
          <h2 className="font-display mt-5 text-3xl md:text-4xl font-black tracking-tighter uppercase leading-tight">
            White paper.
          </h2>
          <p className="mt-3 text-white/60 text-sm md:text-base">
            Déjanos tu correo y te enviamos el PDF al instante.
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          onSubmit={handleSubmit}
          data-testid="whitepaper-form"
          className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8"
        >
          {submitted ? (
            <div
              data-testid="whitepaper-success"
              className="flex flex-col items-center text-center gap-5 py-4"
            >
              <span className="font-mono text-[10px] tracking-widest text-accent">
                /listo
              </span>
              <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter">
                Tu copia está lista.
              </h3>
              <p className="text-white/65 text-sm">
                También enviaremos una copia a{" "}
                <strong className="text-white">{form.email}</strong>.
              </p>
              <a
                href={WHITEPAPER_URL}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="whitepaper-download-link"
                className="btn-block"
              >
                <Download size={14} /> Descargar PDF
              </a>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setForm({ name: "", email: "" });
                }}
                data-testid="whitepaper-reset"
                className="text-xs uppercase tracking-[0.25em] text-white/45 hover:text-white"
              >
                Enviar a otro correo →
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label
                  htmlFor="wp-name"
                  className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                >
                  Nombre*
                </label>
                <input
                  id="wp-name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  data-testid="wp-name-input"
                  placeholder="Tu nombre"
                  className="line-input"
                />
              </div>
              <div className="space-y-1 mt-4">
                <label
                  htmlFor="wp-email"
                  className="text-[10px] uppercase tracking-[0.25em] text-white/50"
                >
                  Correo*
                </label>
                <input
                  id="wp-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="wp-email-input"
                  placeholder="tu@empresa.com"
                  className="line-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="wp-submit"
                className="btn-block w-full justify-center mt-8 disabled:opacity-60 disabled:cursor-wait"
              >
                {submitting ? (
                  <>
                    Procesando <Loader2 className="animate-spin" size={14} />
                  </>
                ) : (
                  <>
                    <Download size={14} /> Quiero el white paper
                  </>
                )}
              </button>
              <p className="mt-5 text-[10px] text-white/40 text-center">
                Sin spam. Sólo regeneración.
              </p>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function MinimalFooter() {
  return (
    <footer className="bg-black border-t border-white/10 py-10">
      <div className="max-w-3xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.25em] text-white/40">
        <div>© 2026 Tony Yarto · Incubatour</div>
        <Link
          to="/"
          className="hover:text-white transition-colors"
          data-testid="footer-back-home"
        >
          ← Volver a Tony Yarto
        </Link>
      </div>
    </footer>
  );
}

export default function TurtopiaLanding() {
  return (
    <div className="bg-black text-white" data-testid="turtopia-root">
      <TurtopiaHeader />
      <main>
        <Hero />
        <Socials />
        <DescargaForm />
      </main>
      <MinimalFooter />
    </div>
  );
}
